import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Debug environment variables
console.log('Supabase URL:', supabaseUrl);
console.log('Supabase Anon Key:', supabaseAnonKey ? 'Present' : 'Missing');

// Validate environment variables
if (!supabaseUrl) {
  throw new Error('Missing VITE_SUPABASE_URL environment variable');
}

if (!supabaseAnonKey) {
  throw new Error('Missing VITE_SUPABASE_ANON_KEY environment variable');
}

// Validate URL format
try {
  new URL(supabaseUrl);
} catch (error) {
  throw new Error(`Invalid VITE_SUPABASE_URL format: ${supabaseUrl}`);
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
  global: {
    headers: {
      'X-Client-Info': 'supabase-js-web',
    },
  },
  db: {
    schema: 'public',
  },
  realtime: {
    params: {
      eventsPerSecond: 2,
    },
  },
});

export type UserProfile = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role_id: string;
  company_id: string;
  department: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Flattened properties to avoid RLS recursion
  role_name?: string;
  role_description?: string;
  company_name?: string;
};

export type Role = {
  id: string;
  name: string;
  description: string;
  created_at: string;
};

export type Company = {
  id: string;
  name: string;
  industry: string;
  size: 'small' | 'medium' | 'large' | 'enterprise';
  subscription_tier: 'basic' | 'professional' | 'enterprise';
  features: CompanyFeature[];
  branding: {
    primary_color: string;
    logo_url?: string;
    custom_domain?: string;
  };
  settings: {
    default_language: 'NL' | 'FR' | 'EN';
    timezone: string;
    currency: string;
  };
  admin_user_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type CompanyFeature = {
  id: string;
  company_id: string;
  feature_key: string;
  feature_name: string;
  is_enabled: boolean;
  configuration?: Record<string, any>;
  created_at: string;
};

export type Location = {
  id: string;
  company_id: string;
  name: string;
  address: string;
  type: 'office' | 'retail' | 'warehouse' | 'production' | 'residential' | 'coliving';
  status: 'active' | 'maintenance_needed' | 'under_maintenance' | 'inactive' | 'overdue';
  last_maintenance: string | null;
  next_maintenance: string | null;
  contact_person_name: string;
  contact_person_phone: string;
  contact_person_email: string;
  contact_person_role: string;
  maintenance_contract_type: 'gas' | 'mazout' | 'heat_pump' | null;
  maintenance_contract_frequency: number | null;
  maintenance_contract_price: number | null;
  maintenance_contract_active: boolean;
  communication_preferences: {
    email: boolean;
    sms: boolean;
    phone: boolean;
  };
  booking_invite_pending: boolean;
  created_at: string;
  updated_at: string;
  vat_entity_id?: string | null;
  property_manager_id?: string | null;
  // Remove nested objects to avoid RLS recursion
  property_manager_name?: string;
  vat_entity_name?: string;
};

export type InterventionRequest = {
  id: string;
  company_id: string;
  service_type: string;
  service_title: string;
  location_id: string | null;
  manual_address: string | null;
  contact_person: string;
  contact_phone: string;
  contact_email: string | null;
  description: string | null;
  is_urgent: boolean;
  urgent_reason: string | null;
  selected_time_slot: string | null;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'action_needed';
  service_price: number;
  handling_fee: number;
  total_cost: number;
  invoiced: boolean;
  invoice_id: string | null;
  created_at: string;
  updated_at: string;
  // Remove nested objects to avoid RLS recursion
  location_name?: string;
  location_address?: string;
};

export type MagicAdmin = {
  id: string;
  email: string;
  name: string;
  is_active: boolean;
  created_at: string;
};

export type SubscriptionAddon = {
  id: string;
  addon_key: string;
  name: string;
  description: string;
  monthly_price: number;
  features: Record<string, any>;
  is_active: boolean;
  created_at: string;
};

export type CompanySubscription = {
  id: string;
  company_id: string;
  base_monthly_price: number;
  addon_ids: string[];
  total_monthly_price: number;
  billing_contact_name: string;
  billing_contact_email: string;
  billing_contact_phone: string;
  billing_address: string;
  vat_number?: string;
  payment_terms_days: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type ServicePricing = {
  id: string;
  company_id: string;
  service_type: string;
  service_name: string;
  base_price: number;
  handling_fee: number;
  is_active: boolean;
  configuration?: {
    handling_time?: number;
    additional_hour_price?: number;
    [key: string]: any;
  };
  created_at: string;
  updated_at: string;
};

export type CompanyBillingSettings = {
  id: string;
  company_id: string;
  monthly_spending_limit: number;
  default_handling_fee: number;
  billing_day_of_month: number;
  auto_invoice_generation: boolean;
  created_at: string;
  updated_at: string;
};

export type MonthlyInvoice = {
  id: string;
  company_id: string;
  invoice_number: string;
  billing_period_start: string;
  billing_period_end: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  due_date: string;
  created_at: string;
  updated_at: string;
};

export type InvoiceLineItem = {
  id: string;
  invoice_id: string;
  item_type: 'subscription' | 'addon' | 'service' | 'handling_fee';
  description: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  reference_id: string | null;
  created_at: string;
};

export type VatEntity = {
  id: string;
  company_id: string;
  name: string;
  vat_number: string;
  address: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
};

// Available features that companies can subscribe to
export const AVAILABLE_FEATURES = {
  // Core features (always included)
  BASIC_BOOKING: {
    key: 'basic_booking',
    name: 'Basic Service Booking',
    description: 'Schedule basic maintenance and repair services',
    category: 'core'
  },
  LOCATION_MANAGEMENT: {
    key: 'location_management',
    name: 'Location Management',
    description: 'Manage multiple properties and locations',
    category: 'core'
  },
  
  // Professional features
  ADVANCED_SCHEDULING: {
    key: 'advanced_scheduling',
    name: 'Advanced Scheduling',
    description: 'Bulk scheduling, recurring appointments, and calendar integration',
    category: 'professional'
  },
  MAINTENANCE_CONTRACTS: {
    key: 'maintenance_contracts',
    name: 'Maintenance Contracts',
    description: 'Set up and manage ongoing maintenance contracts',
    category: 'professional'
  },
  REPORTING_ANALYTICS: {
    key: 'reporting_analytics',
    name: 'Reporting & Analytics',
    description: 'Detailed reports and performance analytics',
    category: 'professional'
  },
  MULTI_USER_MANAGEMENT: {
    key: 'multi_user_management',
    name: 'Multi-User Management',
    description: 'Manage multiple users with role-based access',
    category: 'professional'
  },
  
  // Enterprise features
  API_INTEGRATION: {
    key: 'api_integration',
    name: 'API Integration',
    description: 'Connect with external systems via API',
    category: 'enterprise'
  },
  CUSTOM_BRANDING: {
    key: 'custom_branding',
    name: 'Custom Branding',
    description: 'White-label solution with custom branding',
    category: 'enterprise'
  },
  ADVANCED_INVOICING: {
    key: 'advanced_invoicing',
    name: 'Advanced Invoicing',
    description: 'Complex billing, multiple VAT entities, and financial reporting',
    category: 'enterprise'
  },
  PRIORITY_SUPPORT: {
    key: 'priority_support',
    name: 'Priority Support',
    description: '24/7 priority support with dedicated account manager',
    category: 'enterprise'
  },
  COMPLIANCE_REPORTING: {
    key: 'compliance_reporting',
    name: 'Compliance Reporting',
    description: 'Industry-specific compliance and audit reports',
    category: 'enterprise'
  }
} as const;

// Helper function to test database connection with comprehensive error handling
export const testDatabaseConnection = async (): Promise<{ success: boolean; error?: string; details?: any }> => {
  try {
    console.log('Testing database connection...');
    console.log('Supabase URL:', supabaseUrl);
    console.log('Using anon key:', supabaseAnonKey ? 'Yes' : 'No');
    
    // Test basic connectivity first
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    try {
      const response = await fetch(`${supabaseUrl}/rest/v1/`, {
        method: 'HEAD',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`,
          details: {
            status: response.status,
            statusText: response.statusText,
            url: `${supabaseUrl}/rest/v1/`
          }
        };
      }
    } catch (fetchError: any) {
      clearTimeout(timeoutId);
      
      if (fetchError.name === 'AbortError') {
        return {
          success: false,
          error: 'Connection timeout - Supabase is not responding',
          details: { timeout: true }
        };
      }
      
      return {
        success: false,
        error: `Network error: ${fetchError.message}`,
        details: { networkError: true, originalError: fetchError }
      };
    }
    
    // Test Supabase client query
    const { data, error } = await supabase
      .from('roles')
      .select('count')
      .limit(1);
    
    if (error) {
      console.error('Database query test failed:', error);
      return { 
        success: false, 
        error: `Database error: ${error.message}`,
        details: { supabaseError: error }
      };
    }
    
    console.log('Database connection successful');
    return { success: true };
  } catch (error: any) {
    console.error('Database connection test error:', error);
    
    // Provide specific error messages for common issues
    if (error.message?.includes('Failed to fetch')) {
      return { 
        success: false, 
        error: 'Cannot reach Supabase servers. Please check your internet connection and Supabase project status.',
        details: { networkIssue: true }
      };
    }
    
    if (error.message?.includes('Invalid URL')) {
      return {
        success: false,
        error: 'Invalid Supabase URL configuration. Please check your VITE_SUPABASE_URL environment variable.',
        details: { configurationIssue: true }
      };
    }
    
    return { 
      success: false, 
      error: `Connection error: ${error.message || 'Unknown error'}`,
      details: { unexpectedError: error }
    };
  }
};

// Helper function to check existing user by email - FIXED to avoid RLS recursion
export const checkExistingUser = async (email: string): Promise<UserProfile | null> => {
  try {
    console.log('Checking existing user for email:', email);
    
    // Use explicit column selection to avoid RLS recursion
    const { data, error } = await supabase
      .from('user_profiles')
      .select(`
        id, first_name, last_name, email, role_id, company_id, department, is_active, created_at, updated_at,
        role:roles(name, description),
        company:companies(name)
      `)
      .eq('email', email)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      console.error('Error checking existing user:', error);
      throw error;
    }

    console.log('User check result:', data);
    
    // Process the data to flatten the nested objects
    if (data) {
      const processedData: UserProfile = {
        ...data,
        role_name: data.role?.name || null,
        role_description: data.role?.description || null,
        company_name: data.company?.name || null
      };
      
      // Remove the nested objects to avoid type issues
      delete processedData.role;
      delete processedData.company;
      
      return processedData;
    }
    
    return null;
  } catch (error) {
    console.error('Unexpected error in checkExistingUser:', error);
    throw error;
  }
};

// Helper function to check if user is magic admin with better error handling
export const isMagicAdmin = async (email?: string): Promise<boolean> => {
  try {
    if (!email) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) return false;
      email = user.email;
    }

    console.log('Checking magic admin status for:', email);

    // Test connection first
    const connectionTest = await testDatabaseConnection();
    if (!connectionTest.success) {
      console.error('Cannot check magic admin status - database connection failed:', connectionTest.error);
      return false;
    }

    // Fix: Use explicit select columns and handle empty results properly
    const { data, error } = await supabase
      .from('magic_admins')
      .select('id, email, is_active')
      .eq('email', email)
      .eq('is_active', true)
      .limit(1);

    if (error) {
      console.error('Error checking magic admin status:', error);
      return false;
    }

    // Check if we have any results
    const isAdmin = data && data.length > 0;
    console.log('Magic admin check result:', { email, isAdmin, dataLength: data?.length });
    return isAdmin;
  } catch (error) {
    console.error('Unexpected error in isMagicAdmin:', error);
    return false;
  }
};

// Helper function to fetch all users for a company - FIXED to avoid RLS recursion
export const fetchCompanyUsers = async (companyId: string): Promise<UserProfile[]> => {
  try {
    console.log("Fetching users for company ID:", companyId);
    
    // First check if current user is admin or magic admin
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('No authenticated user found');
      return [];
    }
    
    // Check if user is magic admin
    const isMagicAdminUser = await isMagicAdmin(user.email || undefined);
    
    // Use explicit column selection to avoid RLS recursion
    const { data: profile, error: userError } = await supabase
      .from('user_profiles')
      .select('role_id, company_id')
      .eq('id', user.id)
      .single();

    if (userError) {
      console.error('Error fetching user profile:', userError);
      return [];
    }

    // Get role name separately to avoid RLS recursion
    let isCompanyAdmin = false;
    if (profile?.role_id) {
      const { data: roleData } = await supabase
        .from('roles')
        .select('name')
        .eq('id', profile.role_id)
        .single();
      
      isCompanyAdmin = roleData?.name === 'admin';
    }
    
    console.log("User role check:", { isMagicAdmin: isMagicAdminUser, isCompanyAdmin });
    
    // If user is magic admin or company admin, fetch all users for the company
    if (isMagicAdminUser || isCompanyAdmin) {
      console.log("Admin user - fetching all company users");
      // Use explicit column selection to avoid RLS recursion
      const { data, error } = await supabase
        .from('user_profiles')
        .select(`
          id, first_name, last_name, email, role_id, company_id, department, is_active, created_at, updated_at,
          role:roles(name, description),
          company:companies(name)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching company users:', error);
        throw error;
      }

      console.log("Found users:", data?.length || 0);
      
      // Process the data to flatten the nested objects
      const processedData = data?.map(user => {
        const processedUser: UserProfile = {
          ...user,
          role_name: user.role?.name || null,
          role_description: user.role?.description || null,
          company_name: user.company?.name || null
        };
        
        // Remove the nested objects to avoid type issues
        delete processedUser.role;
        delete processedUser.company;
        
        return processedUser;
      }) || [];
      
      return processedData;
    } else {
      // For non-admin users, only return their own profile
      console.log("Non-admin user - fetching only own profile");
      const { data, error } = await supabase
        .from('user_profiles')
        .select(`
          id, first_name, last_name, email, role_id, company_id, department, is_active, created_at, updated_at,
          role:roles(name, description),
          company:companies(name)
        `)
        .eq('id', user.id)
        .eq('company_id', companyId);

      if (error) {
        console.error('Error fetching user profile:', error);
        return [];
      }

      // Process the data to flatten the nested objects
      const processedData = data?.map(user => {
        const processedUser: UserProfile = {
          ...user,
          role_name: user.role?.name || null,
          role_description: user.role?.description || null,
          company_name: user.company?.name || null
        };
        
        // Remove the nested objects to avoid type issues
        delete processedUser.role;
        delete processedUser.company;
        
        return processedUser;
      }) || [];
      
      return processedData;
    }
  } catch (error) {
    console.error('Error in fetchCompanyUsers:', error);
    throw error;
  }
};

// Helper function to get current user's role - FIXED to avoid RLS recursion
export const getCurrentUserRole = async (userId?: string): Promise<string | null> => {
  try {
    if (!userId) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      userId = user.id;
    }

    // Use explicit column selection to avoid RLS recursion
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('role_id')
      .eq('id', userId)
      .single();

    if (profileError) {
      console.error('Error fetching user profile:', profileError);
      return null;
    }

    if (!profile?.role_id) {
      return null;
    }

    // Fetch role separately to avoid RLS recursion
    const { data: role, error: roleError } = await supabase
      .from('roles')
      .select('name')
      .eq('id', profile.role_id)
      .single();

    if (roleError) {
      console.error('Error fetching role:', roleError);
      return null;
    }

    return role?.name || null;
  } catch (error) {
    console.error('Error getting current user role:', error);
    return null;
  }
};

// Helper function to fetch roles with better error handling
export const fetchRoles = async (): Promise<Role[]> => {
  try {
    console.log('Fetching roles from Supabase...');
    
    const { data, error } = await supabase
      .from('roles')
      .select('*')
      .order('name');

    if (error) {
      console.error('Supabase error fetching roles:', error);
      throw new Error(`Database error: ${error.message}`);
    }

    console.log('Roles fetched successfully:', data);
    return data || [];
  } catch (error) {
    console.error('Error in fetchRoles function:', error);
    throw error;
  }
};

// Helper functions for companies with enhanced error handling
export const fetchCompanies = async (): Promise<Company[]> => {
  try {
    console.log('Fetching companies from Supabase...');
    
    // Test connection first
    const connectionTest = await testDatabaseConnection();
    if (!connectionTest.success) {
      throw new Error(`Database connection failed: ${connectionTest.error}`);
    }

    const { data, error } = await supabase
      .from('companies')
      .select(`
        *,
        features:company_features(*)
      `)
      .order('name');

    if (error) {
      console.error('Error fetching companies:', error);
      throw new Error(`Failed to fetch companies: ${error.message}`);
    }

    console.log('Companies fetched successfully:', data?.length || 0);
    return data || [];
  } catch (error) {
    console.error('Error in fetchCompanies:', error);
    throw error;
  }
};

export const fetchCompanyById = async (companyId: string): Promise<Company | null> => {
  const { data, error } = await supabase
    .from('companies')
    .select(`
      *,
      features:company_features(*)
    `)
    .eq('id', companyId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching company:', error);
    throw error;
  }

  return data;
};

export const createCompany = async (companyData: Omit<Company, 'id' | 'created_at' | 'updated_at' | 'features'>): Promise<Company> => {
  const { data, error } = await supabase
    .from('companies')
    .insert(companyData)
    .select()
    .single();

  if (error) {
    console.error('Error creating company:', error);
    throw error;
  }

  return data;
};

export const updateCompany = async (id: string, companyData: Partial<Company>): Promise<Company> => {
  const { data, error } = await supabase
    .from('companies')
    .update(companyData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating company:', error);
    throw error;
  }

  return data;
};

// Helper functions for subscription addons
export const fetchSubscriptionAddons = async (): Promise<SubscriptionAddon[]> => {
  const { data, error } = await supabase
    .from('subscription_addons')
    .select('*')
    .eq('is_active', true)
    .order('name');

  if (error) {
    console.error('Error fetching subscription addons:', error);
    throw error;
  }

  return data || [];
};

// Helper functions for company subscriptions
export const createCompanySubscription = async (subscriptionData: Omit<CompanySubscription, 'id' | 'created_at' | 'updated_at'>): Promise<CompanySubscription> => {
  const { data, error } = await supabase
    .from('company_subscriptions')
    .insert(subscriptionData)
    .select()
    .single();

  if (error) {
    console.error('Error creating company subscription:', error);
    throw error;
  }

  return data;
};

export const fetchCompanySubscription = async (companyId: string): Promise<CompanySubscription | null> => {
  const { data, error } = await supabase
    .from('company_subscriptions')
    .select('*')
    .eq('company_id', companyId)
    .eq('is_active', true)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching company subscription:', error);
    throw error;
  }

  return data;
};

// Helper functions for service pricing
export const fetchServicePricing = async (companyId: string): Promise<ServicePricing[]> => {
  const { data, error } = await supabase
    .from('service_pricing')
    .select('*')
    .eq('company_id', companyId)
    .eq('is_active', true)
    .order('service_name');

  if (error) {
    console.error('Error fetching service pricing:', error);
    throw error;
  }

  return data || [];
};

export const createServicePricing = async (pricingData: Omit<ServicePricing, 'id' | 'created_at' | 'updated_at'>): Promise<ServicePricing> => {
  const { data, error } = await supabase
    .from('service_pricing')
    .insert(pricingData)
    .select()
    .single();

  if (error) {
    console.error('Error creating service pricing:', error);
    throw error;
  }

  return data;
};

export const updateServicePricing = async (id: string, pricingData: Partial<ServicePricing>): Promise<ServicePricing> => {
  const { data, error } = await supabase
    .from('service_pricing')
    .update(pricingData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating service pricing:', error);
    throw error;
  }

  return data;
};

export const deleteServicePricing = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('service_pricing')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting service pricing:', error);
    throw error;
  }
};

// Helper functions for billing settings
export const fetchCompanyBillingSettings = async (companyId: string): Promise<CompanyBillingSettings | null> => {
  const { data, error } = await supabase
    .from('company_billing_settings')
    .select('*')
    .eq('company_id', companyId)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching billing settings:', error);
    throw error;
  }

  return data;
};

export const updateCompanyBillingSettings = async (companyId: string, settingsData: Partial<CompanyBillingSettings>): Promise<CompanyBillingSettings> => {
  const { data, error } = await supabase
    .from('company_billing_settings')
    .upsert({
      company_id: companyId,
      ...settingsData
    })
    .select()
    .single();

  if (error) {
    console.error('Error updating billing settings:', error);
    throw error;
  }

  return data;
};

// Helper functions for VAT entities
export const fetchVatEntities = async (companyId: string): Promise<VatEntity[]> => {
  const { data, error } = await supabase
    .from('vat_entities')
    .select('*')
    .eq('company_id', companyId)
    .order('name');

  if (error) {
    console.error('Error fetching VAT entities:', error);
    throw error;
  }

  return data || [];
};

export const createVatEntity = async (vatEntityData: Omit<VatEntity, 'id' | 'created_at' | 'updated_at'>): Promise<VatEntity> => {
  // If this is set as default, unset any existing default first
  if (vatEntityData.is_default) {
    await supabase
      .from('vat_entities')
      .update({ is_default: false })
      .eq('company_id', vatEntityData.company_id);
  }

  const { data, error } = await supabase
    .from('vat_entities')
    .insert(vatEntityData)
    .select()
    .single();

  if (error) {
    console.error('Error creating VAT entity:', error);
    throw error;
  }

  return data;
};

export const updateVatEntity = async (id: string, vatEntityData: Partial<VatEntity>): Promise<VatEntity> => {
  // If this is set as default, unset any existing default first
  if (vatEntityData.is_default) {
    await supabase
      .from('vat_entities')
      .update({ is_default: false })
      .eq('company_id', vatEntityData.company_id || '')
      .neq('id', id);
  }

  const { data, error } = await supabase
    .from('vat_entities')
    .update(vatEntityData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating VAT entity:', error);
    throw error;
  }

  return data;
};

export const deleteVatEntity = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('vat_entities')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting VAT entity:', error);
    throw error;
  }
};

// Helper functions for company features
export const fetchCompanyFeatures = async (companyId: string): Promise<CompanyFeature[]> => {
  const { data, error } = await supabase
    .from('company_features')
    .select('*')
    .eq('company_id', companyId);

  if (error) {
    console.error('Error fetching company features:', error);
    throw error;
  }

  return data || [];
};

export const updateCompanyFeature = async (companyId: string, featureKey: string, isEnabled: boolean, configuration?: Record<string, any>): Promise<void> => {
  const { error } = await supabase
    .from('company_features')
    .upsert({
      company_id: companyId,
      feature_key: featureKey,
      feature_name: AVAILABLE_FEATURES[featureKey as keyof typeof AVAILABLE_FEATURES]?.name || featureKey,
      is_enabled: isEnabled,
      configuration: configuration || {}
    });

  if (error) {
    console.error('Error updating company feature:', error);
    throw error;
  }
};

// Helper functions for locations - FIXED to avoid RLS recursion
export const fetchLocations = async (companyId?: string): Promise<Location[]> => {
  let query = supabase
    .from('locations')
    .select('*')
    .order('name');

  if (companyId) {
    query = query.eq('company_id', companyId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching locations:', error);
    throw error;
  }

  return data || [];
};

export const createLocation = async (locationData: Omit<Location, 'id' | 'created_at' | 'updated_at' | 'property_manager_name' | 'vat_entity_name'>): Promise<Location> => {
  const { data, error } = await supabase
    .from('locations')
    .insert(locationData)
    .select('*')
    .single();

  if (error) {
    console.error('Error creating location:', error);
    throw error;
  }

  return data;
};

export const updateLocation = async (id: string, locationData: Partial<Location>): Promise<Location> => {
  const { data, error } = await supabase
    .from('locations')
    .update(locationData)
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    console.error('Error updating location:', error);
    throw error;
  }

  return data;
};

export const deleteLocation = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('locations')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting location:', error);
    throw error;
  }
};

// Helper functions for intervention requests - FIXED to avoid RLS recursion
export const createInterventionRequest = async (requestData: Omit<InterventionRequest, 'id' | 'created_at' | 'updated_at' | 'location_name' | 'location_address'>): Promise<InterventionRequest> => {
  const { data, error } = await supabase
    .from('intervention_requests')
    .insert(requestData)
    .select('*')
    .single();

  if (error) {
    console.error('Error creating intervention request:', error);
    throw error;
  }

  return data;
};

export const fetchInterventionRequests = async (companyId?: string): Promise<InterventionRequest[]> => {
  let query = supabase
    .from('intervention_requests')
    .select('*')
    .order('created_at', { ascending: false });

  if (companyId) {
    query = query.eq('company_id', companyId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching intervention requests:', error);
    throw error;
  }

  return data || [];
};

// Helper function to get current user's company - FIXED to avoid RLS recursion
export const getCurrentUserCompany = async (): Promise<Company | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.log('No authenticated user found');
      return null;
    }

    console.log('Fetching profile for user:', user.id);

    // First, get the user's role_id without nested queries
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('company_id')
      .eq('id', user.id)
      .single();

    if (profileError) {
      console.error('Error fetching user profile:', profileError);
      return null;
    }

    if (!profile?.company_id) {
      console.log('No company_id found for user');
      return null;
    }

    // Fetch company separately to avoid RLS recursion
    const { data: company, error: companyError } = await supabase
      .from('companies')
      .select(`
        *,
        features:company_features(*)
      `)
      .eq('id', profile.company_id)
      .maybeSingle();

    if (companyError) {
      console.error('Error fetching company:', companyError);
      return null;
    }

    console.log('Company found:', company);
    return company;
  } catch (error) {
    console.error('Unexpected error in getCurrentUserCompany:', error);
    return null;
  }
};

// Helper function to check if a feature is enabled for a company
export const isFeatureEnabled = (company: Company, featureKey: string): boolean => {
  if (!company.features) return false;
  
  const feature = company.features.find(f => f.feature_key === featureKey);
  return feature?.is_enabled || false;
};

// Helper function to get features by subscription tier
export const getFeaturesByTier = (tier: Company['subscription_tier']): string[] => {
  const features = Object.values(AVAILABLE_FEATURES);
  
  switch (tier) {
    case 'basic':
      return features.filter(f => f.category === 'core').map(f => f.key);
    case 'professional':
      return features.filter(f => f.category === 'core' || f.category === 'professional').map(f => f.key);
    case 'enterprise':
      return features.map(f => f.key);
    default:
      return [];
  }
};

// Helper function to link user to company - simplified to avoid RLS recursion
export const linkUserToCompany = async (userId: string, companyId: string, userEmail?: string, firstName?: string, lastName?: string): Promise<boolean> => {
  try {
    console.log('Linking user to company:', { userId, companyId });

    // First check if user profile exists using explicit column selection
    const { data: existingProfile, error: fetchError } = await supabase
      .from('user_profiles')
      .select('id, company_id')
      .eq('id', userId)
      .maybeSingle();

    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('Error checking existing profile:', fetchError);
      return false;
    }

    if (existingProfile) {
      // Update existing profile
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ company_id: companyId })
        .eq('id', userId);

      if (updateError) {
        console.error('Error updating user profile:', updateError);
        return false;
      }
    } else {
      // Create new profile with minimal required fields to avoid RLS recursion
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) return false;
      
      // Get a default role_id to avoid null constraint issues
      const { data: defaultRole } = await supabase
        .from('roles')
        .select('id')
        .eq('name', 'user')
        .single();

      // Use provided values or defaults
      const profileData = {
        id: userId,
        first_name: firstName || 'User',
        last_name: lastName || 'Profile',
        email: userEmail || user.email || 'user@example.com',
        company_id: companyId,
        role_id: defaultRole?.id || null,
        company: 'Company', // Simple default to avoid conflicts
        department: 'General', // Simple default to avoid conflicts
        is_active: true
      };

      const { error: insertError } = await supabase
        .from('user_profiles')
        .insert(profileData);

      if (insertError) {
        console.error('Error creating user profile:', insertError);
        return false;
      }
    }

    console.log('Successfully linked user to company');
    return true;
  } catch (error) {
    console.error('Unexpected error linking user to company:', error);
    return false;
  }
};

// Helper function to ensure default subscription exists for a company
export const ensureCompanySubscription = async (companyId: string): Promise<CompanySubscription> => {
  try {
    // First check if subscription already exists
    let subscription = await fetchCompanySubscription(companyId);
    
    if (subscription) {
      return subscription;
    }

    // Get company details to determine default subscription
    const company = await fetchCompanyById(companyId);
    if (!company) {
      throw new Error('Company not found');
    }

    // Create default subscription based on company tier
    const basePrice = company.subscription_tier === 'basic' ? 150 : 
                     company.subscription_tier === 'professional' ? 250 : 350;

    const subscriptionData = {
      company_id: companyId,
      base_monthly_price: basePrice,
      addon_ids: [],
      total_monthly_price: basePrice,
      billing_contact_name: 'Billing Contact',
      billing_contact_email: 'billing@company.com',
      billing_contact_phone: '+32 123 456 789',
      billing_address: 'Company Address',
      payment_terms_days: 30,
      is_active: true
    };

    subscription = await createCompanySubscription(subscriptionData);
    console.log('Created default subscription for company:', companyId);
    
    return subscription;
  } catch (error) {
    console.error('Error ensuring company subscription:', error);
    throw error;
  }
};

// Helper function to ensure default billing settings exist for a company
export const ensureCompanyBillingSettings = async (companyId: string): Promise<CompanyBillingSettings> => {
  try {
    // First check if billing settings already exist
    let billingSettings = await fetchCompanyBillingSettings(companyId);
    
    if (billingSettings) {
      return billingSettings;
    }

    // Get company details to determine default settings
    const company = await fetchCompanyById(companyId);
    if (!company) {
      throw new Error('Company not found');
    }

    // Create default billing settings based on company tier
    const defaultLimit = company.subscription_tier === 'basic' ? 5000 : 
                        company.subscription_tier === 'professional' ? 15000 : 50000;

    const settingsData = {
      company_id: companyId,
      monthly_spending_limit: defaultLimit,
      default_handling_fee: 25.00,
      billing_day_of_month: 1,
      auto_invoice_generation: true
    };

    billingSettings = await updateCompanyBillingSettings(companyId, settingsData);
    console.log('Created default billing settings for company:', companyId);
    
    return billingSettings;
  } catch (error) {
    console.error('Error ensuring company billing settings:', error);
    throw error;
  }
};

// Helper function to ensure default service pricing exists for a company
export const ensureCompanyServicePricing = async (companyId: string): Promise<ServicePricing[]> => {
  try {
    // First check if service pricing already exists
    let servicePricing = await fetchServicePricing(companyId);
    
    if (servicePricing.length > 0) {
      return servicePricing;
    }

    // Create default service pricing
    const defaultServices = [
      { service_type: 'electrical', service_name: 'Electrical Work', base_price: 85.00 },
      { service_type: 'heating', service_name: 'Heating Maintenance', base_price: 95.00 },
      { service_type: 'plumbing', service_name: 'Plumbing & Declogging', base_price: 75.00 },
      { service_type: 'inspection', service_name: 'Electrical Inspection', base_price: 120.00 },
      { service_type: 'safety', service_name: 'Safety Check', base_price: 65.00 },
      { service_type: 'other', service_name: 'Other Services', base_price: 80.00 }
    ];

    const pricingPromises = defaultServices.map(service => 
      supabase
        .from('service_pricing')
        .insert({
          company_id: companyId,
          service_type: service.service_type,
          service_name: service.service_name,
          base_price: service.base_price,
          handling_fee: 25.00,
          is_active: true
        })
        .select()
        .single()
    );

    const results = await Promise.all(pricingPromises);
    servicePricing = results.map(result => result.data).filter(Boolean);
    
    console.log('Created default service pricing for company:', companyId);
    return servicePricing;
  } catch (error) {
    console.error('Error ensuring company service pricing:', error);
    throw error;
  }
};